import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { faArrowAltFromLeft, faTasks, IconDefinition } from '@fortawesome/pro-duotone-svg-icons';
import { Select, Store } from '@ngxs/store';
import { SelectionEvent } from '@progress/kendo-angular-grid';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { BasicBrickComponent } from 'src/app/components/bricks/basic-brick-component';
import { SamwebBrick } from 'src/app/components/bricks/samweb-brick.decorator';
import { getTopmostTableCellElement } from 'src/app/helper/elementAncestorFunctions';
import { shortcutStringBuilder } from 'src/app/helper/shortcutStringBuilder';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { Key } from 'src/app/samwebDescription/samweb/shortcuts/Keys';
import { ShortcutDescription } from 'src/app/samwebDescription/samweb/shortcuts/shortcutDescription';
import {
   CloseCallForwardDialog,
   OpenCallForwardDialog,
   SkillBrowserEntrySelected,
   SkillBrowserSelectNextEntry,
} from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/skill-browser-behaviour/skill-browser-behaviour.actions';
import {
   SkillBrowserBehaviourInstance,
   SkillBrowserBehaviourState,
} from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/skill-browser-behaviour/skill-browser-behaviour.state';
import { StartNewCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { SkillFilterChanged } from 'src/app/store/samwin/samwin-call-list/samwin-call-list.actions';
import { ChangeSkillStatus } from 'src/app/store/samwin/samwin-skills/samwin-skills.actions';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { Skill } from 'src/app/store/samwin/samwin-skills/skill';
import { SkillConstants } from 'src/app/store/samwin/samwin-skills/skill-constants';
import { SkillGroup } from 'src/app/store/samwin/samwin-skills/skill-group';
import { SamwinUserStatusState } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.state';
import { SessionState } from 'src/app/store/session/session.state';

@Component({
   selector: 'samweb-skill-wait-list',
   templateUrl: './skill-browser.component.html',
   styleUrls: ['./skill-browser.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
})
@SamwebBrick({
   brickDefinitionId: 'skillBrowser',
   brickTitleTranslationKey: 'brick.brick_definitions.skill-browser.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.skill-browser.description',
   brickIcon: faTasks,
   minColWidth: 10,
   minRowHeight: 10,
   brickCategory: BrickCategory.CALLING,
})
export class SkillBrowserComponent extends BasicBrickComponent implements OnInit {
   get gridContent(): Element {
      return _(this.element.nativeElement.getElementsByClassName('k-grid-content')).head();
   }
   get gridDisplayHeight(): number {
      return this.element.nativeElement.querySelector('.k-virtual-content')?.offsetHeight;
   }
   get gridRowHeight(): number {
      return this.element.nativeElement.querySelector('td')?.offsetHeight ?? 0;
   }
   get gridPageSize(): number {
      return _.round(this.gridDisplayHeight / this.gridRowHeight);
   }
   @Input()
   public brickInstanceId: string;

   public redirectionIcon: IconDefinition = faArrowAltFromLeft;

   public behaviourState$: Observable<SkillBrowserBehaviourInstance>;

   public skillIdForRedirectionAddressDialog$: Observable<number>;

   public userId: number;

   @Select(SamwinSkillsState.callSkillsInclusiveOverallSkill)
   public skillList$: Observable<SkillGroup[]>;

   @Select(SamwinUserStatusState.isUserLoggedOn)
   public isUserLoggedOn$: Observable<boolean>;

   public selectedKeys$: Observable<number[]>;

   @ViewChild('cellTooltipDirective')
   public cellTooltipDirective: TooltipDirective;

   constructor(private store: Store, private element: ElementRef) {
      super();
   }

   public keyActions: ShortcutDescription[] = [
      {
         shortcutKey: shortcutStringBuilder(Key.ArrowUp),
         action: () => {
            console.debug('[HotKey] SkillBrowser Pressed ArrowUp');
            this.store.dispatch(new SkillBrowserSelectNextEntry(this.brickInstanceId, 'up'));
            this.scrollToSelectedRow();
            return true;
         },
      },
      {
         shortcutKey: shortcutStringBuilder(Key.ArrowDown),
         action: () => {
            console.debug('[HotKey] SkillBrowser Pressed ArrowDown');
            this.store.dispatch(new SkillBrowserSelectNextEntry(this.brickInstanceId, 'down'));
            this.scrollToSelectedRow();
            return true;
         },
      },
      {
         shortcutKey: shortcutStringBuilder(Key.PageUp),
         action: () => {
            console.debug('[HotKey] SkillBrowser Pressed PageUp');
            this.store.dispatch(new SkillBrowserSelectNextEntry(this.brickInstanceId, 'up', this.gridPageSize));
            this.scrollToSelectedRow();
            return true;
         },
      },
      {
         shortcutKey: shortcutStringBuilder(Key.PageDown),
         action: () => {
            console.debug('[HotKey] SkillBrowser Pressed PageDown');
            this.store.dispatch(new SkillBrowserSelectNextEntry(this.brickInstanceId, 'down', this.gridPageSize));
            this.scrollToSelectedRow();
            return true;
         },
      },
   ];

   ngOnInit() {
      this.userId = this.store.selectSnapshot(SessionState.getAuthenticatedUserId);

      this.behaviourState$ = this.store.select(SkillBrowserBehaviourState.skillBrowserBehaviourForInstance(this.brickInstanceId));

      this.skillIdForRedirectionAddressDialog$ = this.behaviourState$.pipe(
         map((x) => x.forwardDialogOpenForSkillId),
         distinctUntilChanged()
      );

      this.selectedKeys$ = this.store.select(SkillBrowserBehaviourState.selectedSkillId).pipe(
         map((fn) => fn(this.brickInstanceId)),
         map((selectedSkill) => [selectedSkill])
      );
   }

   changeSkillStatus(skills: Skill[], $event: any) {
      skills.forEach((skill) => {
         if (skill.skillUserConfiguration.isManageable) {
            this.store.dispatch(new ChangeSkillStatus(skill.skillId, $event.currentTarget.checked, this.brickInstanceId));
         }
      });
      return false;
   }

   openRedirectSkillPopup(skill: Skill) {
      this.store.dispatch(new OpenCallForwardDialog(this.brickInstanceId, skill.skillId));
   }

   closeRedirectSkillPopup() {
      this.store.dispatch(new CloseCallForwardDialog(this.brickInstanceId));
   }

   onSelectionChange(event: SelectionEvent) {
      const selectedRow = _.first(event.selectedRows);
      if (_.isNil(selectedRow)) {
         return;
      }
      const selectedEntry = selectedRow.dataItem as SkillGroup;
      if (selectedEntry.skillId === SkillConstants.OVERALL_SKILL_ID) {
         this.store.dispatch(new SkillBrowserEntrySelected(this.brickInstanceId, null));
         this.store.dispatch(new SkillFilterChanged(null));
         return;
      }
      this.store.dispatch(new SkillBrowserEntrySelected(this.brickInstanceId, selectedEntry.skillId));
      this.store.dispatch(new SkillFilterChanged(_(selectedEntry.skills).head()));
   }

   scrollToSelectedRow() {
      const index = this.store.selectSnapshot(SkillBrowserBehaviourState.selectedSkillIndex)(this.brickInstanceId);
      const selectedRowPosition = this.gridRowHeight * index;
      const halfWindowHeight = this.gridDisplayHeight / 2 - this.gridRowHeight / 2;
      const scrollPosition = selectedRowPosition - halfWindowHeight;
      this.gridContent.scrollTo({ top: scrollPosition });
   }

   public showTooltip(e: MouseEvent): void {
      const element = e.target as HTMLElement;
      const tooltipAnchor = getTopmostTableCellElement(element);
      if (tooltipAnchor && (tooltipAnchor.scrollWidth > tooltipAnchor.parentElement.offsetWidth || tooltipAnchor.offsetWidth < tooltipAnchor.scrollWidth)) {
         this.cellTooltipDirective.toggle(tooltipAnchor);
      } else {
         this.cellTooltipDirective.hide();
      }
   }

   public hideTooltip() {
      this.cellTooltipDirective.hide();
   }

   call(callableContent: string) {
      this.store.dispatch(new StartNewCallAction(callableContent));
   }
}
